import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, Grid, CircularProgress } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { getAllProducts } from '../services/productService';

function QRGenerator() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const allProducts = await getAllProducts();
        setProducts(allProducts);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const generateQRUrl = (productId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/try?p=${productId}`;
  };

  const downloadQR = (productId, productName) => {
    const svg = document.getElementById(`qr-${productId}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `QR_${productName.replace(/\s+/g, '_')}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const printQR = (productId) => {
    const printWindow = window.open('', '', 'height=600,width=800');
    const qrElement = document.getElementById(`qr-card-${productId}`);

    printWindow.document.write('<html><head><title>Stampa QR Code</title>');
    printWindow.document.write('<style>body{font-family:Arial;text-align:center;padding:20px;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(qrElement.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '24px'
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: '#000'
        }}
      >
        Generatore QR Code
      </Typography>
      <Typography
        sx={{
          color: '#666',
          mb: 4
        }}
      >
        Genera e scarica QR code per ogni prodotto da stampare nel negozio
      </Typography>

      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
            <Card
              id={`qr-card-${product.id}`}
              sx={{
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent
                sx={{
                  textAlign: 'center',
                  p: 3
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  {product.name}
                </Typography>
                <Typography
                  sx={{
                    color: '#666',
                    mb: 2
                  }}
                >
                  €{product.price} • {product.category}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#999',
                    mb: 2
                  }}
                >
                  ID: {product.id}
                </Typography>

                <Box
                  sx={{
                    backgroundColor: '#fff',
                    padding: '16px',
                    borderRadius: '8px',
                    mb: 2
                  }}
                >
                  <QRCodeSVG
                    id={`qr-${product.id}`}
                    value={generateQRUrl(product.id)}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#999',
                    mb: 2,
                    wordBreak: 'break-all'
                  }}
                >
                  {generateQRUrl(product.id)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => downloadQR(product.id, product.name)}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#000',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#000',
                        backgroundColor: 'rgba(0,0,0,0.05)'
                      }
                    }}
                  >
                    Scarica
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => printQR(product.id)}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: '#000',
                      '&:hover': {
                        backgroundColor: '#333'
                      }
                    }}
                  >
                    Stampa
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, p: 3, backgroundColor: '#fff', borderRadius: '16px' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          📋 Istruzioni per l'uso
        </Typography>
        <Typography sx={{ mb: 1 }}>
          1. Stampa i QR code usando il pulsante "Stampa" o scaricali con "Scarica"
        </Typography>
        <Typography sx={{ mb: 1 }}>
          2. Posiziona ogni QR code vicino al capo corrispondente nel negozio
        </Typography>
        <Typography sx={{ mb: 1 }}>
          3. I clienti scannerizzeranno il QR con il proprio smartphone
        </Typography>
        <Typography>
          4. L'app si aprirà automaticamente mostrando il prodotto da provare
        </Typography>
      </Box>
    </Box>
  );
}

export default QRGenerator;
